import * as React from 'react';
import {Suspense, useDeferredValue, useRef, useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {DirectUpload} from '@rails/activestorage';
import {Camera, Check, Clapperboard, FileBox, Megaphone, Package, Printer, Radio, ScanLine, Search, Sparkles, Upload, X} from 'lucide-react';
import {
    BUDGETS, Draft, GearPick, INTERESTS, MAX_MODEL_MB, MODEL_EXTENSIONS, SCAN_ACCURACIES, SCAN_LOCATIONS, SCAN_PURPOSES, SCAN_SIZES, SCAN_SURFACES, UploadedModel,
} from './draft';
import {stepsGearQuery} from './__generated__/stepsGearQuery.graphql';
import {stepsTalentQuery} from './__generated__/stepsTalentQuery.graphql';

// Each step edits one slice of the draft. `update` merges a partial draft.
export type StepProps = { draft: Draft; update: (patch: Partial<Draft>) => void };

export const inputClass = 'w-full bg-black border border-gray-800 p-3 text-white focus:border-lumina-accent focus:outline-none transition-colors';
export const labelClass = 'text-xs text-gray-500 uppercase tracking-widest font-bold';

const INTEREST_ICON: Record<string, React.FC<{ className?: string }>> = {
    production: Clapperboard, photography: Camera, scanning: ScanLine, printing: Printer, rental: Package, live_event: Radio, marketing: Megaphone, other: Sparkles,
};

export const WhatStep: React.FC<StepProps> = ({draft, update}) => {
    const toggle = (key: string) => update({interests: draft.interests.includes(key) ? draft.interests.filter((k) => k !== key) : [...draft.interests, key]});
    return (
        <>
            <h2 className="text-2xl font-display font-bold mb-1">What are we making?</h2>
            <p className="text-gray-400 text-sm mb-6">Pick everything that applies.</p>
            <div className="grid sm:grid-cols-2 gap-3">
                {INTERESTS.map(({key, label, blurb}) => {
                    const Icon = INTEREST_ICON[key];
                    const on = draft.interests.includes(key);
                    return (
                        <button key={key} type="button" onClick={() => toggle(key)} aria-pressed={on}
                                className={`relative text-left p-4 border transition-colors ${on ? 'border-lumina-accent bg-lumina-accent/5' : 'border-gray-800 hover:border-gray-600'}`}>
                            {on && <Check className="w-4 h-4 text-lumina-accent absolute top-3 right-3"/>}
                            <Icon className={`w-5 h-5 mb-2 ${on ? 'text-lumina-accent' : 'text-gray-500'}`}/>
                            <div className="text-white font-semibold">{label}</div>
                            <div className="text-gray-500 text-xs">{blurb}</div>
                        </button>
                    );
                })}
            </div>
        </>
    );
};

const GEAR = graphql`
    query stepsGearQuery($search: String) {
        rentalItems(first: 200, search: $search) {
            nodes { id assetTag name category dailyRate kitMembers { id } }
        }
    }`;

const GearPicker: React.FC<{ search: string; picked: GearPick[]; onToggle: (g: GearPick) => void }> = ({search, picked, onToggle}) => {
    const data = useLazyLoadQuery<stepsGearQuery>(GEAR, {search: search || null});
    const items = (data.rentalItems.nodes ?? []).flatMap((n) => (n ? [n] : []));
    const chosen = new Set(picked.map((p) => p.assetTag));
    if (items.length === 0) return <p className="text-gray-500 text-sm">Nothing matches that.</p>;
    return (
        <ul className="max-h-72 overflow-y-auto border border-gray-800 divide-y divide-gray-900">
            {items.map((item) => {
                const on = chosen.has(item.assetTag);
                return (
                    <li key={item.id}>
                        <button type="button" onClick={() => onToggle({assetTag: item.assetTag, name: item.name, pieces: item.kitMembers.length})}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.04] ${on ? 'bg-lumina-accent/5' : ''}`}>
                            <span className={`w-4 h-4 border flex items-center justify-center ${on ? 'bg-lumina-accent border-lumina-accent text-black' : 'border-gray-700'}`}>
                                {on && <Check className="w-3 h-3"/>}
                            </span>
                            <span className="flex-1">
                                <span className="text-white text-sm">{item.name}</span>
                                <span className="block text-[11px] text-gray-500">
                                    {item.category.replace(/_/g, ' ')}{item.kitMembers.length > 0 && ` · ${item.kitMembers.length} pieces`}
                                </span>
                            </span>
                            <span className="text-xs text-gray-500">{item.dailyRate ? `$${item.dailyRate}/day` : 'on request'}</span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

export const GearStep: React.FC<StepProps> = ({draft, update}) => {
    const [search, setSearch] = useState('');
    const deferred = useDeferredValue(search);
    const toggle = (g: GearPick) =>
        update({gear: draft.gear.some((x) => x.assetTag === g.assetTag) ? draft.gear.filter((x) => x.assetTag !== g.assetTag) : [...draft.gear, g]});
    return (
        <>
            <h2 className="text-2xl font-display font-bold mb-1">Which gear?</h2>
            <p className="text-gray-400 text-sm mb-4">Tick anything you already know you want; we'll confirm availability and fill in the rest with you.</p>
            <label className="relative block mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cameras, lenses, kits…" className={`${inputClass} pl-9`}/>
            </label>
            <Suspense fallback={<p className="text-gray-500 text-sm">Loading gear…</p>}>
                <GearPicker search={deferred} picked={draft.gear} onToggle={toggle}/>
            </Suspense>
            {draft.gear.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {draft.gear.map((g) => (
                        <span key={g.assetTag} className="inline-flex items-center gap-1 text-xs bg-gray-900 border border-gray-800 px-2 py-1">
                            {g.name}
                            <button type="button" onClick={() => toggle(g)} className="text-gray-500 hover:text-red-400"><X className="w-3 h-3"/></button>
                        </span>
                    ))}
                </div>
            )}
            <label className={`mt-4 flex items-start gap-3 p-3 border cursor-pointer ${draft.consult ? 'border-lumina-purple bg-lumina-purple/5' : 'border-gray-800 hover:border-gray-600'}`}>
                <input type="checkbox" checked={draft.consult} onChange={(e) => update({consult: e.target.checked})} className="mt-1"/>
                <span>
                    <span className="block text-white text-sm font-semibold">Not sure what you need? Request a consultation.</span>
                    <span className="block text-xs text-gray-500">We'll call or meet, walk through the shoot and put a package together.</span>
                </span>
            </label>
        </>
    );
};

export const ModelsStep: React.FC<StepProps> = ({draft, update}) => {
    const fileInput = useRef<HTMLInputElement>(null);
    // Keep a ref to the latest models so async upload callbacks patch the current list.
    const latest = useRef(draft.models);
    latest.current = draft.models;
    const setModels = (fn: (m: UploadedModel[]) => UploadedModel[]) => {
        latest.current = fn(latest.current);
        update({models: latest.current});
    };

    const addFiles = (files: FileList | null) => {
        if (!files) return;
        Array.from(files).forEach((file) => {
            const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
            const entry: UploadedModel = {name: file.name, size: file.size, progress: 0};
            if (!MODEL_EXTENSIONS.includes(ext)) entry.error = `We can't read ${ext || 'that'} files (${MODEL_EXTENSIONS.join(' ')})`;
            else if (file.size > MAX_MODEL_MB * 1024 * 1024) entry.error = `Over ${MAX_MODEL_MB} MB`;
            setModels((m) => [...m, entry]);
            if (entry.error) return;

            // Straight to storage; the signed id goes on the engagement.
            const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', {
                directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) setModels((m) => m.map((x) => (x.name === file.name && !x.signedId ? {...x, progress: e.loaded / e.total} : x)));
                    });
                },
            });
            upload.create((err, blob) => {
                setModels((m) => m.map((x) => (x.name === file.name && !x.signedId
                    ? (err ? {...x, error: 'Upload failed, try again'} : {...x, signedId: blob.signed_id, progress: 1})
                    : x)));
            });
        });
    };

    return (
        <>
            <h2 className="text-2xl font-display font-bold mb-1">Upload your models</h2>
            <p className="text-gray-400 text-sm mb-4">{MODEL_EXTENSIONS.join(', ')} · up to {MAX_MODEL_MB} MB each. Skip this if you don't have files yet.</p>
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                 onClick={() => fileInput.current?.click()}
                 className="border-2 border-dashed border-gray-700 hover:border-lumina-accent p-8 text-center cursor-pointer transition-colors">
                <Upload className="w-6 h-6 mx-auto text-gray-500 mb-2"/>
                <p className="text-sm text-gray-300">Drop files here or click to choose</p>
                <input ref={fileInput} type="file" multiple accept={MODEL_EXTENSIONS.join(',')} className="hidden"
                       onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}/>
            </div>
            {draft.models.length > 0 && (
                <ul className="mt-3 border border-gray-800 divide-y divide-gray-900 text-sm">
                    {draft.models.map((m, i) => (
                        <li key={`${m.name}-${i}`} className="px-3 py-2 flex items-center gap-3">
                            <FileBox className="w-4 h-4 text-gray-500"/>
                            <span className="flex-1 min-w-0">
                                <span className="text-white truncate block">{m.name}</span>
                                <span className="text-[11px] text-gray-500">{(m.size / 1024 / 1024).toFixed(1)} MB{m.error && <span className="text-red-400"> · {m.error}</span>}</span>
                                {!m.signedId && !m.error && (
                                    <span className="block h-1 bg-gray-800 mt-1"><span className="block h-1 bg-lumina-accent" style={{width: `${Math.round(m.progress * 100)}%`}}/></span>
                                )}
                            </span>
                            {m.signedId && <Check className="w-4 h-4 text-emerald-400"/>}
                            <button type="button" onClick={() => setModels((list) => list.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4"/></button>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
};

export const ScanStep: React.FC<StepProps> = ({draft, update}) => {
    const scan = draft.scan;
    const set = (patch: Partial<typeof scan>) => update({scan: {...scan, ...patch}});
    const toggleIn = (key: 'surfaces' | 'purpose', value: string) =>
        set({[key]: scan[key].includes(value) ? scan[key].filter((k) => k !== value) : [...scan[key], value]} as Partial<typeof scan>);
    const chip = (on: boolean) => `px-3 py-1.5 border text-xs ${on ? 'border-lumina-accent text-white bg-lumina-accent/5' : 'border-gray-800 text-gray-300 hover:border-gray-600'}`;
    const tile = (on: boolean) => `text-left p-3 border text-sm ${on ? 'border-lumina-accent bg-lumina-accent/5' : 'border-gray-800 hover:border-gray-600'}`;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-display font-bold mb-1">What are we scanning?</h2>
                <p className="text-gray-400 text-sm">This decides which scanner we bring (MIRACO vs. MetroX), whether we need scanning spray, and how long it takes.</p>
            </div>
            <div>
                <label className={labelClass}>Size</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                    {SCAN_SIZES.map((s) => (
                        <button key={s.key} type="button" onClick={() => set({objectSize: s.key})} className={tile(scan.objectSize === s.key)}>
                            <span className="block text-white">{s.label}</span>
                            <span className="block text-[11px] text-gray-500">{s.blurb}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                    <label className={labelClass}>How many objects?</label>
                    <input type="number" min={1} value={scan.objectCount} onChange={(e) => set({objectCount: e.target.value})} className={inputClass} placeholder="1"/>
                </div>
                <div className="space-y-2">
                    <label className={labelClass}>Where?</label>
                    <select value={scan.location} onChange={(e) => set({location: e.target.value})} className={inputClass}>
                        <option value="">Not sure yet</option>
                        {SCAN_LOCATIONS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </select>
                </div>
            </div>
            <div>
                <label className={labelClass}>Surfaces <span className="text-gray-700">(pick all that apply)</span></label>
                <div className="flex flex-wrap gap-2 mt-2">
                    {SCAN_SURFACES.map((s) => (
                        <button key={s.key} type="button" aria-pressed={scan.surfaces.includes(s.key)} onClick={() => toggleIn('surfaces', s.key)}
                                className={chip(scan.surfaces.includes(s.key))} title={s.blurb}>
                            {s.label}{s.blurb && <span className="text-gray-500"> · {s.blurb}</span>}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <label className={labelClass}>How accurate does it need to be?</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                    {SCAN_ACCURACIES.map((a) => (
                        <button key={a.key} type="button" onClick={() => set({accuracy: a.key})} className={tile(scan.accuracy === a.key)}>
                            <span className="block text-white">{a.label}</span>
                            <span className="block text-[11px] text-gray-500">{a.blurb}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <label className={labelClass}>What's it for? <span className="text-gray-700">(pick all that apply)</span></label>
                <div className="flex flex-wrap gap-2 mt-2">
                    {SCAN_PURPOSES.map((p) => (
                        <button key={p.key} type="button" aria-pressed={scan.purpose.includes(p.key)} onClick={() => toggleIn('purpose', p.key)} className={chip(scan.purpose.includes(p.key))}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <label className={labelClass}>Anything else about the objects?</label>
                <textarea rows={2} value={scan.notes} onChange={(e) => set({notes: e.target.value})} className={inputClass}
                          placeholder="Fragile, mounted, moving parts, deadline for the files…"/>
            </div>
        </div>
    );
};

const TALENT = graphql`
    query stepsTalentQuery {
        talent { id name slug headline roles imageUrl }
    }`;

const CrewPicker: React.FC<{ picked: string[]; onToggle: (slug: string) => void }> = ({picked, onToggle}) => {
    const data = useLazyLoadQuery<stepsTalentQuery>(TALENT, {});
    if (data.talent.length === 0) return <p className="text-gray-500 text-sm">No crew profiles are published yet — skip this step.</p>;
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            {data.talent.map((t) => {
                const on = picked.includes(t.slug);
                return (
                    <button key={t.id} type="button" onClick={() => onToggle(t.slug)} aria-pressed={on}
                            className={`relative flex items-center gap-3 text-left p-3 border transition-colors ${on ? 'border-lumina-accent bg-lumina-accent/5' : 'border-gray-800 hover:border-gray-600'}`}>
                        {t.imageUrl
                            ? <img src={t.imageUrl} alt="" className="w-12 h-12 rounded-full object-cover border border-gray-800"/>
                            : <span className="w-12 h-12 rounded-full bg-gray-900 border border-gray-800"/>}
                        <span className="min-w-0">
                            <span className="block text-white font-semibold truncate">{t.name}</span>
                            <span className="block text-[11px] text-gray-500 truncate">{t.headline ?? t.roles.map((r) => r.toLowerCase().replace(/_/g, ' ')).join(', ')}</span>
                        </span>
                        {on && <Check className="w-4 h-4 text-lumina-accent absolute top-3 right-3"/>}
                    </button>
                );
            })}
        </div>
    );
};

export const CrewStep: React.FC<StepProps> = ({draft, update}) => (
    <>
        <h2 className="text-2xl font-display font-bold mb-1">Anyone you'd like on it?</h2>
        <p className="text-gray-400 text-sm mb-4">Optional. Pick crew you've worked with or liked the look of; we'll check their availability.</p>
        <Suspense fallback={<p className="text-gray-500 text-sm">Loading crew…</p>}>
            <CrewPicker picked={draft.crew} onToggle={(slug) => update({crew: draft.crew.includes(slug) ? draft.crew.filter((s) => s !== slug) : [...draft.crew, slug]})}/>
        </Suspense>
    </>
);

export const DetailsStep: React.FC<StepProps> = ({draft, update}) => (
    <div className="space-y-5">
        <h2 className="text-2xl font-display font-bold">Tell us about it</h2>
        <div className="space-y-2">
            <label className={labelClass}>Project name <span className="text-gray-700">(optional)</span></label>
            <input value={draft.projectName} onChange={(e) => update({projectName: e.target.value})} className={inputClass} placeholder="Spring campaign, short film, trade show…"/>
        </div>
        <div className="space-y-2">
            <label className={labelClass}>What do you need?</label>
            <textarea rows={5} value={draft.details} onChange={(e) => update({details: e.target.value})} className={inputClass}
                      placeholder="Deliverables, locations, crew you already have, deadlines — whatever you know so far."/>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
                <label className={labelClass}>When does it start?</label>
                <input type="date" value={draft.startsOn} onChange={(e) => update({startsOn: e.target.value})} className={inputClass}/>
            </div>
            <div className="space-y-2">
                <label className={labelClass}>Budget range</label>
                <select value={draft.budget} onChange={(e) => update({budget: e.target.value})} className={inputClass}>
                    <option value="">Prefer not to say</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
            </div>
        </div>
    </div>
);

export const ContactStep: React.FC<StepProps & { signedInAs?: string | null }> = ({draft, update, signedInAs}) => (
    <div className="space-y-5">
        <h2 className="text-2xl font-display font-bold">How do we reach you?</h2>
        {signedInAs && <p className="text-xs text-gray-500">Signed in as {signedInAs}; this will be filed under your account.</p>}
        <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2"><label className={labelClass}>Name</label>
                <input value={draft.name} onChange={(e) => update({name: e.target.value})} className={inputClass}/></div>
            <div className="space-y-2"><label className={labelClass}>Company <span className="text-gray-700">(optional)</span></label>
                <input value={draft.company} onChange={(e) => update({company: e.target.value})} className={inputClass}/></div>
            <div className="space-y-2"><label className={labelClass}>Email</label>
                <input type="email" value={draft.email} onChange={(e) => update({email: e.target.value})} className={inputClass}/></div>
            <div className="space-y-2"><label className={labelClass}>Phone <span className="text-gray-700">(optional)</span></label>
                <input value={draft.phone} onChange={(e) => update({phone: e.target.value})} className={inputClass}/></div>
        </div>
    </div>
);
